import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth-store';

/**
 * The staff presence heartbeat.
 *
 * Mounted once in the app shell. While this browser tab is VISIBLE and the
 * operator is signed in, it tells svc-monitor every minute which back-office
 * page they are on; the shift board then calls anybody whose last beat is
 * within three minutes online.
 *
 * What it deliberately does NOT observe is the whole point: no screen, no
 * camera, no keyboard, no desktop. The whole signal is "this tab is open, on
 * this route", and everything identifying — username, role, address — is
 * resolved server-side from the token, never sent from here.
 *
 * `visibilitychange` rather than `blur`/`focus`: a supervisor with the console
 * on a second monitor while they work elsewhere IS at their desk with it open,
 * and an unfocused-but-visible window should stay on the board. A hidden tab
 * (minimised, or another tab in front) stops, and three minutes later the row
 * ages out on its own — nothing has to be sent to say "I left".
 */
export const STAFF_HEARTBEAT_INTERVAL_MS = 60_000;

/** Longer than the path column svc-monitor stores, so a deep route is trimmed
 *  here rather than refused there. */
const MAX_PATH_LENGTH = 128;

export interface StaffHeartbeatDeps {
  /** Test seam — defaults to the real POST. */
  send?: (path: string) => Promise<unknown>;
  /** Test seam — defaults to `document.visibilityState`. */
  isVisible?: () => boolean;
  intervalMs?: number;
}

function defaultSend(path: string): Promise<unknown> {
  return api.post('/api/v1/staff-online/heartbeat', { path });
}

function defaultIsVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

export function useStaffHeartbeat(deps: StaffHeartbeatDeps = {}): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { pathname } = useLocation();

  const send = deps.send ?? defaultSend;
  const isVisible = deps.isVisible ?? defaultIsVisible;
  const intervalMs = deps.intervalMs ?? STAFF_HEARTBEAT_INTERVAL_MS;

  // The route changes far more often than the beat, and re-arming the timer on
  // every navigation would turn a one-minute cadence into a beat per click.
  // Held in a ref so the effect below depends on the session alone.
  const pathRef = React.useRef(pathname);
  pathRef.current = pathname;

  const sendRef = React.useRef(send);
  sendRef.current = send;
  const visibleRef = React.useRef(isVisible);
  visibleRef.current = isVisible;

  React.useEffect(() => {
    if (!isAuthenticated) return;

    let stopped = false;

    const beat = (): void => {
      if (stopped) return;
      if (!visibleRef.current()) return;
      // Fire and forget. A failed beat costs one minute of board accuracy; a
      // rejected promise reaching the console on every network blip costs the
      // operator's attention, and there is nothing here for them to do about it.
      void Promise.resolve(sendRef.current(pathRef.current.slice(0, MAX_PATH_LENGTH))).catch(
        () => {},
      );
    };

    // Immediately, so somebody who has just signed in appears on the board now
    // rather than in a minute.
    beat();
    const timer = setInterval(beat, intervalMs);
    // A tab coming back into view beats at once too, for the same reason: it
    // may have been hidden for longer than the online window.
    const onVisibility = (): void => {
      if (visibleRef.current()) beat();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, intervalMs]);
}
